import { MenuPageShell } from '@/components/printMenu/MenuPageShell'
import type { Service } from '@/services/firestore/types'
import {
  ADDON_BLOCK_TITLES,
  groupAddOnServices,
} from '@/utils/addOnCatalog'
import { formatCents } from '@/utils/format'

type AdditionalServicesPageProps = {
  addOnServices: Service[]
  pageNumber: number
  totalPages: number
}

const DESIGN_BLOCK_IDS = ['designs', 'per-nails', 'per-items'] as const
const REMOVAL_BLOCK_ID = 'removal'

const REMOVAL_FOOTNOTES = [
  'Soaking / e-file (what is good for the natural nails).',
  'Leaving 10–20% of existing product to ensure we are never thinning down your actual nail.',
] as const

function formatAddOnPrice(priceCents: number) {
  const formatted = formatCents(priceCents)
  return formatted.startsWith('$') ? `+${formatted}` : formatted
}

function PriceRow({ service }: { service: Service }) {
  const sublabel =
    service.description.trim() !== service.name.trim() ? service.description : undefined

  return (
    <div className="flex items-start justify-between gap-3 border-b border-hairline/50 py-[6px] last:border-b-0">
      <div className="min-w-0">
        <p className="font-sans text-[length:var(--menu-fs-sm)] leading-snug text-body">{service.name}</p>
        {sublabel ? (
          <p className="mt-0.5 font-sans text-[length:var(--menu-fs-xs)] leading-snug text-muted">{sublabel}</p>
        ) : null}
      </div>
      <span className="shrink-0 pt-px font-sans text-[length:var(--menu-fs-sm)] font-medium tabular-nums text-body">
        {formatAddOnPrice(service.priceCents)}
      </span>
    </div>
  )
}

function ServiceBlock({
  blockId,
  services,
  footnotes,
}: {
  blockId: string
  services: Service[]
  footnotes?: readonly string[]
}) {
  const label = ADDON_BLOCK_TITLES[blockId] ?? blockId

  return (
    <section>
      <header className="mb-2 border-b border-hairline/50 pb-2 text-center">
        <p className="font-sans text-[length:var(--menu-fs-xs)] font-semibold uppercase tracking-[0.22em] text-primary">
          {label}
        </p>
      </header>
      <div className="mt-2">
        {services.map((service) => (
          <PriceRow key={service.id} service={service} />
        ))}
      </div>
      {footnotes?.length ? (
        <div className="mt-3 space-y-1.5 border-t border-hairline/60 pt-3">
          {footnotes.map((note) => (
            <p key={note} className="font-sans text-[length:var(--menu-fs-xs)] leading-relaxed text-muted">
              {note}
            </p>
          ))}
        </div>
      ) : null}
    </section>
  )
}

function AdditionalColumn({
  blockIds,
  groups,
}: {
  blockIds: readonly string[]
  groups: Map<string, Service[]>
}) {
  return (
    <div className="flex flex-col gap-5">
      {blockIds.map((blockId) => {
        const services = groups.get(blockId)
        if (!services?.length) {
          return null
        }

        return (
          <ServiceBlock
            key={blockId}
            blockId={blockId}
            footnotes={blockId === REMOVAL_BLOCK_ID ? REMOVAL_FOOTNOTES : undefined}
            services={services}
          />
        )
      })}
    </div>
  )
}

export function AdditionalServicesPage({
  addOnServices,
  pageNumber,
  totalPages,
}: AdditionalServicesPageProps) {
  const groups = groupAddOnServices(addOnServices)

  return (
    <MenuPageShell pageNumber={pageNumber} totalPages={totalPages}>
      <div className="grid min-h-0 flex-1 grid-cols-2 items-start gap-x-[10mm]">
        <AdditionalColumn blockIds={DESIGN_BLOCK_IDS} groups={groups} />
        <AdditionalColumn blockIds={[REMOVAL_BLOCK_ID]} groups={groups} />
      </div>
    </MenuPageShell>
  )
}
